/**
 * QuickReassignModal — pulls the active users/roles list from `config.role`
 * (Wave 3 the full `config.userRoles` will be available) and lets a supervisor
 * hand a session off to a different officer.
 */

import { useState } from 'react';
import { Button, ModalShell } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { FIXTURE_PRESENCE } from '../fixtures';

export interface QuickReassignModalProps {
  open: boolean;
  onClose: () => void;
  onReassign: (toInitials: string, reason: string) => void;
}

export function QuickReassignModal({ open, onClose, onReassign }: QuickReassignModalProps): JSX.Element | null {
  useConfig(); // reserve hook for Wave 3 `config.userRoles`
  const [toInitials, setToInitials] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Quick reassign"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!toInitials || !reason}
            onClick={() => { onReassign(toInitials, reason); onClose(); }}
          >
            Reassign
          </Button>
        </>
      }
    >
      <p style={{ fontSize: 13, color: 'var(--ink-600)', margin: 0 }}>
        Reassigns the currently-selected session to another FSR/Officer. All reassignments
        emit a SessionEvent.
      </p>

      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
        Reassign to
        <select
          value={toInitials}
          onChange={(e) => setToInitials(e.target.value)}
          style={{ width: '100%', padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', marginTop: 4, fontFamily: 'inherit' }}
        >
          <option value="">Choose an officer</option>
          {FIXTURE_PRESENCE.filter((p) => p.presence !== 'offline').map((p) => (
            <option key={p.initials} value={p.initials}>
              {p.initials} — {p.name} · {p.role} · {p.claims}c
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
        Reason
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Rolla at capacity, St. James has headroom"
          style={{ width: '100%', padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', marginTop: 4, fontFamily: 'inherit' }}
        />
      </label>
    </ModalShell>
  );
}
